import React from 'react';
import { PillIcon, ShoppingBagIcon, ZapIcon } from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
type ExperienceKey = 'shop' | 'innova' | 'rx';
interface ExperienceOption {
  id: ExperienceKey;
  label: string;
  description: string;
  icon: React.ReactNode;
  iconClass: string;
  animation: {
    scale?: number[];
    y?: number[];
    rotate?: number[];
  };
}
const experiences: ExperienceOption[] = [{
  id: 'shop',
  label: 'Shop',
  description: 'All products',
  icon: <ShoppingBagIcon strokeWidth={1.9} data-id="element-2555" data-mp="8tesaptm2wk3" />,
  iconClass: 'bg-[#eaf2ff] text-[#245ca8]',
  animation: {
    y: [0, -2, 0]
  }
}, {
  id: 'innova',
  label: 'NOW',
  description: '120 min',
  icon: <ZapIcon strokeWidth={2.1} data-id="element-2556" data-mp="hv4la03563jz" />,
  iconClass: 'bg-[#fff1c9] text-[#ce7b00]',
  animation: {
    scale: [1, 1.1, 1]
  }
}, {
  id: 'rx',
  label: 'RX',
  description: 'Health services',
  icon: <PillIcon strokeWidth={2} data-id="element-2557" data-mp="1t98m9q85cryj" />,
  iconClass: 'bg-[#f0eaff] text-[#6a4eb5]',
  animation: {
    rotate: [0, -7, 0]
  }
}];
interface MobileExperienceSwitcherProps {
  activeExperience?: ExperienceKey;
  isCompact?: boolean;
  onSelect?: (experience: ExperienceKey) => void;
}
export function MobileExperienceSwitcher({
  activeExperience = 'shop',
  onSelect
}: MobileExperienceSwitcherProps) {
  const shouldReduceMotion = useReducedMotion();
  return <nav aria-label="Shopping experience" className="mt-1 bg-transparent px-4 pb-1.5 pt-1" data-id="element-2558" data-mp="337dwkju8bhmo">
      <div className="grid h-[54px] w-full grid-cols-3 rounded-xl border border-[#dce3ec] bg-[#f4f7fb] p-1" data-id="element-2559" data-mp="2y7jactlefg7y">
        {experiences.map(experience => {
        const isActive = experience.id === activeExperience;
        return <button key={experience.id} type="button" aria-current={isActive ? 'page' : undefined} aria-label={`${experience.label} experience`} onClick={() => onSelect?.(experience.id)} className={`relative flex min-w-0 items-center gap-2 rounded-lg px-2 text-left transition-[background-color,border-color] duration-150 focus:outline-none focus:ring-2 focus:ring-[#103d91] focus:ring-inset ${isActive ? 'border border-[#e4e9f0] bg-white' : 'border border-transparent bg-transparent'}`} data-id="element-2560" data-mp="2jhlmyfvamujt">
              <motion.span animate={shouldReduceMotion ? {
            scale: 1,
            y: 0,
            rotate: 0
          } : experience.animation} transition={{
            duration: 0.75,
            repeat: shouldReduceMotion ? 0 : Infinity,
            repeatDelay: 3.2,
            ease: [0.22, 1, 0.36, 1]
          }} className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md ${experience.iconClass}`} aria-hidden="true" data-id="element-2561" data-mp="1vt5en0b7fufc">
                <span className="[&>svg]:h-3.5 [&>svg]:w-3.5" data-id="element-2562" data-mp="2eqygdza3dyb5">{experience.icon}</span>
              </motion.span>
              <span className="min-w-0 leading-none" data-id="element-2563" data-mp="32gamure1bs9r">
                <span className={`block truncate text-xs font-bold ${isActive ? 'text-[#1E2B55]' : 'text-[#61708a]'}`} data-id="element-2564" data-mp="37a5dkes257ic">{experience.label}</span>
                <span className="mt-1 block truncate text-[9px] font-medium text-[#7a879b]" data-id="element-2565" data-mp="vs0l7i02j6mv">{experience.description}</span>
              </span>
              {isActive && <span className="absolute bottom-0 left-3 right-3 h-0.5 rounded-full bg-[#2d6fcd]" aria-hidden="true" data-id="element-2566" data-mp="14ldkd1oq74he" />}
            </button>;
      })}
      </div>
    </nav>;
}